import { FC } from 'react';
import { Popup } from '@codex/organisms';
import styled from 'styled-components';
import { Ribbon } from '@codex/basics';

const StyledPopup = styled(Popup)`
  > div {
    margin: 0 5px;
  }
`;

const PopupContentWrapper = styled.div`
  margin: 10px 0 20px 0;
`;

const TopLogo = styled.img`
  width: 180px;
  position: relative;
  right: 16px;
  bottom: 3px;
`;

const MonthlyPricePaymentMessage = styled.div`
  margin: 0 20px 8px 20px;
`;

const TopCreditMessage = styled.div`
  margin: 0 20px 8px 20px;
`;

const BottomCreditMessage = styled.div`
  margin: 0 20px 8px 20px;
  a {
    color: ${({ theme }) => theme.colors.link.default};
    text-decoration: none;
    :hover {
      ${({ theme }) => theme.colors.link.hover};
    }
  }
`;

const SeparatorLine = styled.hr`
  display: block;
  height: 1px;
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.shades[5]};
  margin: 8px 20px;
  padding: 0;
`;

const RepresentativeExampleHeader = styled.div`
  text-align: center;
  font-weight: bold;
  margin: 0 20px 10px 20px;
  color: ${({ theme }) => theme.colors.shades[3]};
`;

const RepresentativeExampleMessage = styled.div`
  font-weight: bold;
  margin: 0 20px 8px 20px;
  color: ${({ theme }) => theme.colors.shades[3]};
`;

const RepresentativeExampleNote = styled.div`
  margin: 0 20px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.shades[3]};
`;

const BottomLink = styled.div`
  text-align: center;
  margin-bottom: 4px;
  a {
    font-size: 11px;
    color: ${({ theme }) => theme.colors.link.default};
    text-decoration: none;
    :hover {
      ${({ theme }) => theme.colors.link.hover};
    }
  }
`;

const FindOutMoreCreditLink = styled.div`
  margin: 0 20px;
  a {
    text-decoration: none;
    color: ${({ theme }) => theme.colors.link.default};
    :hover {
      ${({ theme }) => theme.colors.link.hover};
    }
  }
`;

const RibbonWrapper = styled.div`
  box-sizing: border-box;
  position: relative;
  height: 20px;
  margin-top: 10px;
`;

const StyledRibbon = styled(Ribbon)`
  box-sizing: border-box;
  top: 0;
  height: 20px;
  width: calc(100% + 7px);
  padding: 2px 0 2px 15px;
  text-align: left;
  background-color: #723f89;
`;

type Props = {
  onPopupCloseAction?(): void,
  popupTitle: string,
  topLogoUrl: string,
  monthlyPricePaymentMessage: string,
  topCreditMessage: string,
  bottomCreditMessage: string,
  representativeExampleHeader: string,
  representativeExampleMessage: string,
  representativeExampleNote: string,
  creditAccountFaqsLink: string,
  creditAccountInformationLink: string,
  findOutMoreCreditLink: string,
  specialCreditOfferMessage: string
};

const ProductCreditPopup: FC<Props> = ({ onPopupCloseAction = () => {}, popupTitle, topLogoUrl,
  monthlyPricePaymentMessage, topCreditMessage, bottomCreditMessage, representativeExampleHeader,
  representativeExampleMessage, representativeExampleNote, creditAccountFaqsLink,
  creditAccountInformationLink, findOutMoreCreditLink, specialCreditOfferMessage }) => (

    <StyledPopup
      onClose={() => {
        onPopupCloseAction();
      }}
      title={popupTitle}
    >
      <PopupContentWrapper>
        <TopLogo
          src={topLogoUrl}
          alt={'top logo'}
        />
        {!!monthlyPricePaymentMessage && (
        <MonthlyPricePaymentMessage
          dangerouslySetInnerHTML={{ __html: monthlyPricePaymentMessage }}
        />
        )}
        <TopCreditMessage
          dangerouslySetInnerHTML={{ __html: topCreditMessage }}
        />
        <BottomCreditMessage
          dangerouslySetInnerHTML={{ __html: bottomCreditMessage }}
        />
        {!!representativeExampleMessage && (
        <>
          <SeparatorLine />
          <RepresentativeExampleHeader>
            {representativeExampleHeader}
          </RepresentativeExampleHeader>
          <RepresentativeExampleMessage>
            {representativeExampleMessage}
          </RepresentativeExampleMessage>
          <RepresentativeExampleNote>
            {representativeExampleNote}
          </RepresentativeExampleNote>
          <SeparatorLine />
          <BottomLink
            dangerouslySetInnerHTML={{ __html: creditAccountFaqsLink }}
          />
          <BottomLink
            dangerouslySetInnerHTML={{ __html: creditAccountInformationLink }}
          />
        </>
        )}
        {!representativeExampleMessage && (
          <FindOutMoreCreditLink
            dangerouslySetInnerHTML={{ __html: findOutMoreCreditLink }}
          />
        )}
        {!!specialCreditOfferMessage && (
          <RibbonWrapper>
            <StyledRibbon side={'left'} type={'info'}>
              <span dangerouslySetInnerHTML={{ __html: specialCreditOfferMessage }} />
            </StyledRibbon>
          </RibbonWrapper>
        )}
      </PopupContentWrapper>
    </StyledPopup>
);

ProductCreditPopup.displayName = 'ProductCreditPopup';

export default ProductCreditPopup;
